import type { Components } from '@/api/generated/api';
import * as billingApi from '@/api/resources/billing.api';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { useReferralAttribution } from '@/composables/referral/useReferralAttribution';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { usePermissions } from '@/composables/usePermissions';
import { useToast } from '@/composables/useToast';
import { AnalyticsEvents } from '@/services/analytics/events';
import {
  CheckoutOfferSource,
  withCheckoutOfferSourceInReturnPath,
  type CheckoutOfferSource as OfferSource,
} from '@/services/analytics/checkout';
import { isPurchasableProduct, type BillingInterval } from '@/utils/billing';
import { daysUntil } from '@/utils/date.utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { toValue, type MaybeRefOrGetter } from 'vue';
import { useRoute } from 'vue-router';
import { invalidateBillingQueries } from './billingQueryInvalidation';

type BillingStatus = MaybeRefOrGetter<Components.Schemas.BillingStatusDto | undefined>;
type CheckoutOptions = {
  quantity?: number;
  source?: Components.Schemas.CheckoutSource;
  billingInterval?: BillingInterval;
};

function useBillingMutationContext(billingStatus: BillingStatus) {
  const { currentTenant } = useCurrentTenant();
  const { hasTenantOwnerAccess } = usePermissions();
  const queryClient = useQueryClient();
  const { catchError } = useToast();
  return {
    canManage: () => !!toValue(billingStatus)?.isBillingActive && hasTenantOwnerAccess.value,
    tenantId: () => currentTenant.value?.id,
    refresh: (tenantId: number) => invalidateBillingQueries(queryClient, tenantId),
    catchError,
  };
}

function useBillingMutation<TArgs extends unknown[], TData>(
  context: ReturnType<typeof useBillingMutationContext>,
  request: (...args: TArgs) => Promise<TData>,
  options: {
    beforeRequest?: () => Promise<void>;
    refresh?: boolean | ((...args: TArgs) => boolean);
    onSuccess?: () => void;
  } = {},
) {
  const mutation = useMutation({
    mutationFn: ({ args }: { args: TArgs; tenantId: number }) => request(...args),
    onSuccess: async (_data, { args, tenantId }) => {
      const refresh = typeof options.refresh === 'function' ? options.refresh(...args) : options.refresh;
      if (refresh !== false) await context.refresh(tenantId);
      options.onSuccess?.();
    },
  });
  return async (...args: TArgs): Promise<TData | null> => {
    const tenantId = context.tenantId();
    if (tenantId == null || !context.canManage()) return null;
    return context.catchError(async () => {
      await options.beforeRequest?.();
      // Attribution can yield long enough for the user to switch tenants or lose owner access.
      if (context.tenantId() !== tenantId || !context.canManage()) return null;
      return mutation.mutateAsync({ args, tenantId });
    });
  };
}

function getDefaultCheckoutOfferSource(status: Components.Schemas.BillingStatusDto | undefined): OfferSource | null {
  if (!status) return null;
  if (status.isTrialActive) return CheckoutOfferSource.TrialAdminPlans;
  const trialEnds = daysUntil(status.trialEndsAt);
  const isBasisAfterTrial =
    typeof trialEnds === 'number' &&
    trialEnds <= 0 &&
    !status.hasAnySubscriptionHistory &&
    !status.products.some((product) => product.isCurrentlyActive);
  return isBasisAfterTrial ? CheckoutOfferSource.BasisAdminPlans : null;
}

function useSubscriptionMutations(context: ReturnType<typeof useBillingMutationContext>) {
  const { trackEvent } = useAnalytics();
  const { attributeIfPossible } = useReferralAttribution();
  return {
    switchTenantPlan: useBillingMutation(
      context,
      (productId: number, quantity?: number, billingInterval?: BillingInterval) =>
        billingApi.switchTenantPlan({ productTypeId: productId, quantity, billingInterval }),
      {
        beforeRequest: () => attributeIfPossible('checkout'),
        onSuccess: () => trackEvent(AnalyticsEvents.billing.planSwitched),
      },
    ),
    tenantMarkTrialExpiredSeen: useBillingMutation(context, billingApi.markTenantTrialExpiredSeen, {
      onSuccess: () => {
        trackEvent(AnalyticsEvents.billing.basePlanChosen);
        trackEvent(AnalyticsEvents.funnelSummary.t2f);
      },
    }),
    tenantExtendTrial: useBillingMutation(context, billingApi.extendTenantTrial),
  };
}

function useOfferMutations(context: ReturnType<typeof useBillingMutationContext>) {
  const route = useRoute();
  return {
    createTenantOffer: useBillingMutation(
      context,
      (productId: number, address: Components.Schemas.UpsertTenantInvoiceAddressDto, opts?: { quantity?: number }) =>
        billingApi.createTenantOffer({ productTypeId: productId, quantity: opts?.quantity, address }),
    ),
    updateTenantOfferAddress: useBillingMutation(context, billingApi.updateTenantOfferAddress),
    acceptTenantOfferViaInvoice: useBillingMutation(
      context,
      (offerId: number, opts?: { refreshBillingState?: boolean }) => {
        void opts; // Controls cache refresh in onSuccess, not the API request.
        return billingApi.acceptTenantOfferViaInvoice(offerId);
      },
      { refresh: (_offerId, opts) => opts?.refreshBillingState !== false },
    ),
    acceptTenantOfferOnline: useBillingMutation(
      context,
      (offerId: number, returnPath?: string) =>
        billingApi.acceptTenantOfferOnline(offerId, returnPath ?? route.fullPath),
      { refresh: false },
    ),
    deleteTenantOffer: useBillingMutation(context, billingApi.deleteTenantOffer),
  };
}

function useCheckoutMutations(context: ReturnType<typeof useBillingMutationContext>, billingStatus: BillingStatus) {
  const { attributeIfPossible } = useReferralAttribution();
  const route = useRoute();
  const checkoutTenant = useBillingMutation(
    context,
    (productId: number, opts?: CheckoutOptions & { returnPath?: string; checkoutOfferSource?: OfferSource | null }) => {
      const offerSource = opts?.checkoutOfferSource ?? getDefaultCheckoutOfferSource(toValue(billingStatus));
      const returnPath = withCheckoutOfferSourceInReturnPath(opts?.returnPath ?? route.fullPath, offerSource);
      return billingApi.checkoutTenant({
        productTypeId: productId,
        quantity: opts?.quantity,
        billingInterval: opts?.billingInterval,
        source: opts?.source,
        returnPath,
      });
    },
    { beforeRequest: () => attributeIfPossible('checkout'), refresh: false },
  );
  return {
    checkoutTenant,
    checkoutAnyProduct: (product: Components.Schemas.ProductTypeDto, quantity?: number) =>
      isPurchasableProduct(product.product) ? checkoutTenant(product.id, { quantity }) : Promise.resolve(null),
    checkoutTenantViaInvoice: useBillingMutation(
      context,
      (productId: number, opts?: CheckoutOptions) =>
        billingApi.checkoutTenantViaInvoice({ productTypeId: productId, ...opts }),
      { beforeRequest: () => attributeIfPossible('checkout') },
    ),
    updateTenantInvoiceAddress: useBillingMutation(context, billingApi.updateTenantInvoiceAddress, { refresh: false }),
  };
}

/** Register actions without starting billing reads. The caller supplies its existing status. */
export function useBillingMutations(billingStatus: BillingStatus) {
  const context = useBillingMutationContext(billingStatus);
  return {
    ...useSubscriptionMutations(context),
    ...useOfferMutations(context),
    ...useCheckoutMutations(context, billingStatus),
    refreshBillingStates: async () => {
      const tenantId = context.tenantId();
      if (tenantId != null) await context.refresh(tenantId);
    },
  };
}
